import React, { useState } from 'react';
import fr from 'facet-rest';
import Sentry from 'Integrations/Sentry';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import DataContainer from 'Components/UI/DataContainer';
import MessageSkeleton from 'Components/ComponentLibrary/Atoms/Skeletons/MessageSkeleton';
import RichTextDisplayPicker from 'Organisms/MessageList/MessageListItem/MessageContent/Content/RichTextDisplayPicker';
import ThreadRoutingDetails
  from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/MessagesPanel/components/MessagesPanelContent/components/ThreadRoutingDetails';
import PropTypes from 'prop-types';
import { FDBox, FDStack } from '@facet-wealth/design';
import { displayErrorMessageWithChecks } from 'Context/SnackbarContext/utils';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { RouteMessageButton } from 'Organisms/MessageList/MessageListItem/MessageRoute/RouteMessageButton';
import { useManualRoutingSubmit } from 'Hooks/useRefreshEvent';

const MessageWithThreadRoutingContent = ({ messageResource, onLoadComplete }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [threadRoutingDetails, setThreadRoutingDetails] = React.useState(null);
  const { displayErrorMessage } = useSnackbarContext();

  const fetchThreadRoutingDetails = async () => {
    try {
      const threadRoutingDetailsResourceFromRelationship = await messageResource.withRelationship(fr.relationships.threadRoutingDetails.name,
        async (rel) => {
          const [threadRoutingDetailsResource] = await rel.link('related').get();
          return threadRoutingDetailsResource;
        },
        () => null);
      setThreadRoutingDetails(threadRoutingDetailsResourceFromRelationship);
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to fetch thread routing details');
      Sentry.createSentryErrorLog(err);
    }
  };

  useComponentDidMount(async () => {
    setIsLoading(true);
    await fetchThreadRoutingDetails();
    setIsLoading(false);
    onLoadComplete();
  });

  useManualRoutingSubmit(async () => {
    await fetchThreadRoutingDetails();
  }, []);


  return (
    <DataContainer
      loading={isLoading}
      skeleton={MessageSkeleton}
      component={() => (
        <FDStack direction={'column'} spacing={3}>
          <RichTextDisplayPicker content={messageResource.attr('content')} resource={messageResource} />
          <ThreadRoutingDetails resource={threadRoutingDetails} />
          <FDBox>
            {messageResource.withRelationship(
              fr.relationships.manualMessageRouting.name,
              (rel) => (rel.meta('disabled') ? null : <RouteMessageButton relationship={rel} />),
              () => null,
            )}
          </FDBox>
        </FDStack>
      )}
    />
  );
};

MessageWithThreadRoutingContent.defaultProps = {
  onLoadComplete: () => {},
};

MessageWithThreadRoutingContent.propTypes = {
  messageResource: PropTypes.instanceOf(fr.Resource).isRequired,
  onLoadComplete: PropTypes.func,
};

export default MessageWithThreadRoutingContent;
